import { datadogRum } from "@datadog/browser-rum";
import { useMantineTheme } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { ReactNode } from "react";
import { useMutation } from "react-query";
import { colors } from "theme";

import DelightedStarsFeedback from "components/DelightedStarsFeedback";
import Flex from "components/Flex";
import { CheckCircle } from "components/Icons/CheckCircle";
import { ErrorIcon } from "components/Icons/Error";
import { StarIcon } from "components/Icons/Star";
import Text from "components/Text";

import { submitFeedback } from "api/submitFeedback";
import { clientConfigAtom } from "atoms/clientConfig";
import { ratingAtom } from "atoms/feedback";
import { sessionIdAtom } from "atoms/sessionId";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useEventTracking } from "hooks/useEventTracking";
import { shouldRetry } from "utils/networkRequestHelper";

import { clientIdAtom } from "../../atoms/clientId";

type FeedbackProps = {
  children: ReactNode;
  visible?: boolean;
  showFeedbackText?: boolean;
};

export const FeedbackDoneText = () => {
  const { other } = useMantineTheme();
  const { t } = useClientTranslation();
  return (
    <Flex direction="row" align="center" justify="center">
      <CheckCircle fill={other.primary[80]} />
      <Text sizeVariant="md" style={{ marginLeft: "8px", fontWeight: 500, color: other.primary[80] }}>
        {t("done.feedback_thanks")}
      </Text>
    </Flex>
  );
};

export const PromptForFeedback = () => {
  const { other } = useMantineTheme();
  const { t } = useClientTranslation();
  return (
    <Flex>
      <Text sizeVariant="lg" weight={500} color={other.primary[80]}>
        {t("done.rate")}
      </Text>
    </Flex>
  );
};

export const Feedback = ({ children, visible = true, showFeedbackText = true }: FeedbackProps) => {
  const { trackEvent } = useEventTracking();
  const [rating, setRating] = useAtom(ratingAtom);
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const { enableDelightedFeedback } = useAtomValue(clientConfigAtom);
  const { other } = useMantineTheme();
  const { t } = useClientTranslation();

  const { mutate, isError } = useMutation(submitFeedback, {
    onSuccess: ({ data }) => {
      trackEvent({ name: "Rating", properties: { score: data.rating } });
      setRating({
        value: data.rating,
        submitted: true,
      });
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Failed to submit feedback",
      });
    },
    retry: shouldRetry,
  });

  return (
    <Flex flex="1" gap={16} justify="space-between">
      {children}
      {visible && (
        <>
          {enableDelightedFeedback ? (
            <DelightedStarsFeedback clientId={clientId} sessionId={sessionId} />
          ) : (
            <Flex
              px={24}
              pt={24}
              pb={36}
              align="center"
              justify="center"
              gap={20}
              style={{
                background: colors.grey[3],
                position: "sticky",
                bottom: 0,
                textAlign: "center",
              }}
            >
              {showFeedbackText && (rating.submitted ? <FeedbackDoneText /> : <PromptForFeedback />)}

              <Flex direction="row" gap={36} style={{ justifySelf: "flex-end" }}>
                {Array.from(Array(5).keys()).map((_, index) => (
                  <div
                    key={index}
                    role="button"
                    aria-label={`${index + 1} star rating`}
                    onClick={() => {
                      mutate({ rating: index + 1, sessionId, clientId });
                    }}
                  >
                    <StarIcon fill={rating.value > index ? other.primary[60] : colors.grey[10]} />
                  </div>
                ))}
              </Flex>
              {isError && (
                <Flex direction="row">
                  <ErrorIcon />
                  <Text ml={8} sizeVariant="base" style={{ color: colors.red[40] }}>
                    {t("done.rate.error")}
                  </Text>
                </Flex>
              )}
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
};
